import { useEffect, useRef } from "react";

import TimeInput from "@components/General/TimeInput";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { DashboardService } from "@services/DashboardService";
import { PresenteeismConciliationDetailService } from "@services/PresenteeismConciliationDetailService";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import { SmallText, Text } from "@shared/designSystem/Texts";
import UserCard from "@shared/designSystem/UserCard";
import designSystemStyles from "@shared/styles/designSystemStyles";

import {
    PendingCheckoutRowRequest,
    usePresenteeismManualCheckoutModalViewModel,
} from "./PresenteeismManualCheckoutModalViewModel";

interface PresenteeismManualCheckoutModalProps {
    request: PendingCheckoutRowRequest | null;
    isOpen: boolean;
    onClose: () => void;
    onSaved?: () => void;
    service: PresenteeismConciliationDetailService;
    dashboardService: DashboardService;
    timezone: string;
}

const PresenteeismManualCheckoutModal: React.FC<PresenteeismManualCheckoutModalProps> = ({
    request,
    isOpen,
    onClose,
    onSaved,
    service,
    dashboardService,
    timezone,
}) => {
    const viewModel = usePresenteeismManualCheckoutModalViewModel({
        isOpen,
        request,
        service,
        dashboardService,
        timezone,
    });

    const timeInputRef = useRef<{ focus: () => void; commitAndGetValue: () => string }>(null);

    useEffect(() => {
        if (!isOpen || viewModel.loading || viewModel.error) {
            return;
        }
        // El modal recién termina de montar/renderizar el campo en este punto
        // (mientras carga muestra el spinner), por eso el foco se pide acá.
        const timeoutID = window.setTimeout(() => timeInputRef.current?.focus(), 50);
        return () => window.clearTimeout(timeoutID);
    }, [isOpen, viewModel.loading, viewModel.error]);

    const handleSubmit = async () => {
        // El Enter dentro del campo de hora dispara esto igual que el click en Guardar,
        // así que hace falta esta guarda extra (el botón ya se deshabilita solo, pero
        // el teclado no pasa por ese disabled).
        if (viewModel.saving) {
            return;
        }
        // Se lee el valor tal cual está escrito en este momento (en vez de confiar en el
        // estado ya confirmado del campo), para no depender del orden en que el navegador
        // dispara el blur del input vs. el click del botón.
        const committedHour = timeInputRef.current?.commitAndGetValue() ?? "";
        const saved = await viewModel.handleSave(committedHour);
        if (saved) {
            onSaved?.();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Fichar salida".localized()}
            maxWidth="560px"
        >
            <VerticalContainer width="100%" gap={designSystemStyles.gapLg}>
                {viewModel.loading ? (
                    <QontyLoading
                        text={"Cargando información del colaborador...".localized()}
                        withQonty={true}
                    />
                ) : viewModel.error ? (
                    <QontyEmptyState text={viewModel.error} />
                ) : (
                    <VerticalContainer width="100%" gap={designSystemStyles.gapLg} alignment="left">
                        <UserCard
                            name={request?.collaboratorName || "Colaborador".localized()}
                            imageURL={request?.photo || undefined}
                        />

                        <HorizontalContainer width="100%" gap={designSystemStyles.gapLg}>
                            <DetailField
                                label={"Edificio".localized()}
                                value={viewModel.buildingName}
                            />
                            <DetailField
                                label={"Fecha".localized()}
                                value={request?.fechaDisplay}
                            />
                        </HorizontalContainer>

                        <VerticalContainer width="160px" margin="0" gap={designSystemStyles.gapXxxSm} alignment="left">
                            <SmallText text={"Horario de salida".localized()} />
                            <TimeInput
                                innerRef={timeInputRef}
                                initValue={viewModel.hour}
                                setHour={viewModel.setHour}
                                onValidityChange={viewModel.handleHourValidityChange}
                                clampOutOfRange={false}
                                onEnterKey={handleSubmit}
                                style={{ height: "50px", border: "1px solid #DDDFE3", borderRadius: "9px" }}
                            />
                        </VerticalContainer>

                        {viewModel.hourWarning ? (
                            <Text text={viewModel.hourWarning} color={designSystemStyles.colorError} />
                        ) : null}

                        {viewModel.saveError ? (
                            <Text text={viewModel.saveError} color={designSystemStyles.colorError} />
                        ) : null}
                    </VerticalContainer>
                )}

                <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapMd}>
                    <HollowedButton text={"Cancelar".localized()} onClick={onClose} disabled={viewModel.saving} width="140px" />
                    <NormalButton
                        text={"Guardar".localized()}
                        onClick={handleSubmit}
                        disabled={!viewModel.canSubmit}
                        loading={viewModel.saving}
                        width="140px"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

export default PresenteeismManualCheckoutModal;
